import React, { useState } from 'react'
import { Button } from '@sureco/design-system'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'
import ConfirmCellChangesModal from './ConfirmCellChangesModal'

interface EditActionsProps {
  undoLastEdit: () => void
  editCount: number
  dirtyRows: CensusFileRowGridData[]
  handleSubmit: (rows: CensusFileRowGridData[]) => void
  cleanAllRows: () => void
  submitCorrections: (rows: CensusFileRowGridData[]) => Promise<void>
  updateMode: boolean
  setMode?: (mode: 'edit' | 'submit') => void
  setLoading?: (loading: boolean) => void
  pendingIgnoreCreates?: Array<{
    censusRowId: number
    fieldName: string
    ignoredNewValue: string | number | boolean
    displayEmployeeId: string
    displayName: string
  }>
}

function InlineEditActionButtons({
  undoLastEdit,
  editCount,
  dirtyRows,
  handleSubmit,
  cleanAllRows,
  submitCorrections,
  updateMode,
  setMode,
  setLoading: setPageLoading,
  pendingIgnoreCreates,
}: EditActionsProps) {
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)

  if (editCount === 0) return null

  const handleSaveEdits = async () => {
    setLoading(true)
    setPageLoading?.(true)
    try {
      await submitCorrections(dirtyRows)
      handleSubmit(dirtyRows)
      cleanAllRows()
      // Only leave edit mode and close the modal after a successful save
      setMode?.('submit')
      setModalVisible(false)
    } catch {
      // submitCorrections already showed an error toast — keep the modal open
    } finally {
      setLoading(false)
      setPageLoading?.(false)
    }
  }

  return (
    <>
      <Button onClick={undoLastEdit} size="large" variant="danger">
        Undo Last Edit
      </Button>
      <Button
        onClick={() => setModalVisible(true)}
        variant="primary"
        size="large"
      >
        Save Edits ({editCount})
      </Button>

      <ConfirmCellChangesModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onConfirm={handleSaveEdits}
        loading={loading}
        closable={!loading}
        dirtyRows={dirtyRows}
        updateMode={updateMode}
        pendingIgnoreCreates={pendingIgnoreCreates}
      />
    </>
  )
}

export default InlineEditActionButtons
